/**
 * "Детали диалога" card — BANT qualification + dialogue details
 * (greeting, objection handling, closing technique).
 */

import { useTranslation } from 'react-i18next';
import { isObjectionHandled } from '@aica/core';
import { cn } from '@/lib/utils';
import {
  AnalysisSection,
  Tag,
  scoreTextColor,
  buildBantRows,
  type XMLAnalysisResult,
} from './analysis-utils';

interface DialogueSectionProps {
  result: XMLAnalysisResult;
}

export function DialogueSection({ result }: DialogueSectionProps) {
  const { t } = useTranslation('records');

  const bant = result.dialogue?.needsQualification?.bant;
  const bantRows = buildBantRows(bant, t);
  const bantIdentified = bantRows.filter((r) => r.identified).length;
  const bantTotal = bantRows.length;
  const hasBant = bantTotal > 0;

  const hasDialogueDetails = !!(
    result.dialogue &&
    (result.dialogue.greetingIntro ||
      (result.dialogue.objectionHandling?.objections &&
        result.dialogue.objectionHandling.objections.length > 0) ||
      result.dialogue.closingTechnique)
  );

  if (!hasBant && !hasDialogueDetails) return null;

  return (
    <AnalysisSection
      title={t('records.analysisPanel.dialogueDetails', 'Dialogue Details')}
      summary={
        hasBant ? (
          <span className="font-mono text-xs font-semibold text-warning">
            {bantIdentified}/{bantTotal}
          </span>
        ) : undefined
      }
    >
      {/* BANT Qualification */}
      {hasBant && (
        <div className="mb-3">
          <div className="mb-1.5 font-mono text-micro font-semibold uppercase tracking-wide text-fg-muted">
            {t('records.analysisPanel.bantQualification', 'BANT Qualification')}
          </div>
          <div className="flex flex-col gap-1">
            {bantRows.map((row) => (
              <div key={row.label}>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-fg-muted">{row.label}</span>
                  <div className="flex items-center gap-1.5">
                    {row.score !== undefined && (
                      <span
                        className={cn('font-mono text-micro', scoreTextColor(row.score))}
                      >
                        {row.score}
                      </span>
                    )}
                    <Tag variant={row.identified ? 'success' : 'danger'}>
                      {row.tagLabel}
                    </Tag>
                  </div>
                </div>
                {row.quote && (
                  <p className="mt-0.5 pl-1 text-micro italic text-fg-faint">
                    &ldquo;{row.quote}&rdquo;
                  </p>
                )}
              </div>
            ))}
          </div>
          {result.dialogue?.needsQualification?.totalScore !== undefined && (
            <div className="mt-2 flex items-center justify-between px-1 text-xs">
              <span className="font-medium text-fg-muted">
                {t('records.analysisPanel.bant.totalScore', 'Total Score')}
              </span>
              <span
                className={cn(
                  'font-mono font-semibold',
                  scoreTextColor(result.dialogue.needsQualification.totalScore)
                )}
              >
                {result.dialogue.needsQualification.totalScore}
              </span>
            </div>
          )}
          {result.dialogue?.needsQualification?.feedback && (
            <p className="mt-1 px-1 text-micro italic text-fg-faint">
              {result.dialogue.needsQualification.feedback}
            </p>
          )}
        </div>
      )}

      {/* Dialogue Details */}
      {hasDialogueDetails && (
        <div
          className={cn(
            'flex flex-col gap-2 text-xs',
            hasBant && 'border-t border-border pt-3'
          )}
        >
          {/* Greeting Intro */}
          {result.dialogue!.greetingIntro && (
            <div>
              <div className="flex items-center justify-between rounded bg-bg-surface px-2.5 py-1.5">
                <span className="text-fg-muted">
                  {t('records.analysisPanel.dialogue.greeting', 'Greeting')}
                </span>
                <div className="flex items-center gap-1.5">
                  {result.dialogue!.greetingIntro.score !== undefined && (
                    <span
                      className={cn(
                        'font-mono text-micro font-semibold',
                        scoreTextColor(result.dialogue!.greetingIntro.score)
                      )}
                    >
                      {result.dialogue!.greetingIntro.score}
                    </span>
                  )}
                  {result.dialogue!.greetingIntro.used !== undefined && (
                    <Tag
                      variant={result.dialogue!.greetingIntro.used ? 'success' : 'danger'}
                    >
                      {result.dialogue!.greetingIntro.used
                        ? t('records.analysisPanel.dialogue.used', 'Used')
                        : t('records.analysisPanel.dialogue.notUsed', 'Not Used')}
                    </Tag>
                  )}
                </div>
              </div>
              {result.dialogue!.greetingIntro.feedback && (
                <p className="mt-0.5 px-2.5 text-xs text-fg-faint">
                  {result.dialogue!.greetingIntro.feedback}
                </p>
              )}
            </div>
          )}

          {/* Objection Handling */}
          {result.dialogue!.objectionHandling?.objections &&
            result.dialogue!.objectionHandling.objections.length > 0 && (
              <div>
                <div className="mb-1 flex items-center justify-between rounded bg-bg-surface px-2.5 py-1.5">
                  <span className="text-fg-muted">
                    {t(
                      'records.analysisPanel.dialogue.objectionHandling',
                      'Objection Handling'
                    )}
                  </span>
                  {result.dialogue!.objectionHandling.averageScore !== undefined && (
                    <span
                      className={cn(
                        'font-mono font-semibold',
                        scoreTextColor(result.dialogue!.objectionHandling.averageScore)
                      )}
                    >
                      {result.dialogue!.objectionHandling.averageScore.toFixed(0)}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1 px-2.5">
                  {result.dialogue!.objectionHandling.objections.map((obj, idx) => {
                    const handled = isObjectionHandled(obj);
                    return (
                      <div key={idx} className="py-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-fg-secondary">
                            {obj.type}
                          </span>
                          {obj.score !== undefined ? (
                            <span
                              className={cn(
                                'font-mono text-micro',
                                scoreTextColor(obj.score)
                              )}
                            >
                              {obj.score}
                            </span>
                          ) : (
                            /* Analyses stored before per-objection scoring existed
                               carry only the legacy `handled` boolean. Without
                               this chip the row degraded to "objection type +
                               the customer's own words" — an objection nobody
                               answered looked exactly like one answered well.
                               The verdict comes from `isObjectionHandled`, the
                               same read-side rule the CRM report, the alerts
                               engine and AnalysisRepository already use, so all
                               four surfaces cannot disagree about one record. */
                            <Tag variant={handled ? 'success' : 'danger'}>
                              {handled
                                ? t(
                                    'records.analysisPanel.dialogue.objectionHandled',
                                    'Handled'
                                  )
                                : t(
                                    'records.analysisPanel.dialogue.objectionNotHandled',
                                    'Not handled'
                                  )}
                            </Tag>
                          )}
                        </div>
                        {obj.quote && (
                          <p className="mt-0.5 text-xs text-fg-faint">“{obj.quote}”</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Closing Technique */}
          {result.dialogue!.closingTechnique && (
            <div>
              <div className="flex items-center justify-between rounded bg-bg-surface px-2.5 py-1.5">
                <span className="text-fg-muted">
                  {t(
                    'records.analysisPanel.dialogue.closingTechnique',
                    'Closing Technique'
                  )}
                </span>
                {typeof result.dialogue!.closingTechnique.score === 'number' ? (
                  <span
                    className={cn(
                      'font-mono font-semibold',
                      scoreTextColor(result.dialogue!.closingTechnique.score)
                    )}
                  >
                    {result.dialogue!.closingTechnique.score}
                  </span>
                ) : (
                  // score is null, not absent, when closing was never applicable to
                  // this call — `!== undefined` used to let null through and render
                  // it as a blank, danger-colored slot. Em-dash matches ScoreCell's
                  // convention for a scoreless metric.
                  <span className="font-mono font-semibold text-fg-faint">—</span>
                )}
              </div>
              {result.dialogue!.closingTechnique.techniqueUsed && (
                <p className="mt-0.5 px-2.5 text-xs text-fg-secondary">
                  {result.dialogue!.closingTechnique.techniqueUsed}
                </p>
              )}
              {result.dialogue!.closingTechnique.feedback && (
                <p className="mt-0.5 px-2.5 text-xs italic text-fg-faint">
                  {result.dialogue!.closingTechnique.feedback}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </AnalysisSection>
  );
}
